export type LegalDocType = 'service' | 'privacy' | 'privacy-consent' | 'marketing' | 'refund' | 'email'

export interface LegalDoc {
  type: LegalDocType
  title: string
  summary?: string
  updatedAt: string
}

export interface PrivacyCollectionRow {
  category: string
  items: string
  purpose: string
  retention: string
}

export const COMPANY = {
  name: '(주)함우리',
  legalName: '주식회사 함우리',
  owner: '김승식',
  bizNumber: '185-81-02287',
  address: '전북특별자치도 전주시 덕진구 기지로 77, 202동 2803호',
  /** 전국대표번호. 카드사 심사 하단 유선번호 */
  phone: '1833-7489',
  serviceName: '장기요양지원센터',
  mallName: '함우리몰',
  email: 'hamwoori25@naver.com',
  hours: '평일 09:30 – 17:30',
  motto: '함께 이루는 혁신, 함께 나누는 가치',
} as const

/** 주식회사 함우리 대표이사 직인 (caion font/hamwoori_sign_3.png 와 동일) */
export const HAMWOORI_SEAL = '/images/brand/hamwoori-seal.png'

export const PRIVACY_COLLECTION_ROWS: PrivacyCollectionRow[] = [
  {
    category: '회원 가입',
    items: '아이디, 비밀번호, 이름, 이메일, 휴대폰 번호',
    purpose: '회원 식별·인증, 본인 확인, 고객 상담 및 공지 전달',
    retention: '회원 탈퇴 시까지 (관련 법령에 따라 보존 시 해당 기간)',
  },
  {
    category: '기관 정보',
    items: '기관명, 기관 기호',
    purpose: '장기요양기관 맞춤 서비스 제공, 임금·회계·고시 지원',
    retention: '회원 탈퇴 시까지',
  },
  {
    category: '서비스 이용',
    items: '접속 IP, 접속 일시, 서비스 이용 기록, 기기 정보',
    purpose: '서비스 안정성 확보, 부정 이용 방지, 통계 분석',
    retention: '3년',
  },
  {
    category: '상담·문의',
    items: '상담 내용, 첨부 파일(임금대장 등)',
    purpose: '무료 상담·진단 서비스 제공 및 답변',
    retention: '상담 완료 후 3년',
  },
]

export const LEGAL_DOCS: Record<LegalDocType, LegalDoc> = {
  service: {
    type: 'service',
    title: '이용약관',
    summary: '장기요양지원센터 회원 서비스 이용에 관한 기본 약관입니다.',
    updatedAt: '2026-06-22',
  },
  privacy: {
    type: 'privacy',
    title: '개인정보처리방침',
    summary: '장기요양지원센터의 개인정보 처리 원칙과 절차를 안내합니다.',
    updatedAt: '2026-06-22',
  },
  'privacy-consent': {
    type: 'privacy-consent',
    title: '개인정보 수집·이용 동의',
    summary: '회원가입 및 서비스 제공을 위해 필요한 개인정보 수집·이용에 대한 동의 내용입니다.',
    updatedAt: '2026-06-22',
  },
  marketing: {
    type: 'marketing',
    title: '마케팅·광고성 정보 수신 동의',
    summary: '신규 서비스, 이벤트, 장기요양 관련 유용한 정보를 안내하기 위한 선택 동의입니다.',
    updatedAt: '2026-06-22',
  },
  refund: {
    type: 'refund',
    title: '환불 기준',
    summary: '함우리몰 주문 취소·반품·환불 기준입니다. 결제는 토스페이먼츠이고, 이전 계좌이체 주문은 그 기록대로 환불합니다.',
    updatedAt: '2026-10-29',
  },
  email: {
    type: 'email',
    title: '이메일 무단수집 거부',
    summary: '이 사이트에 공개된 이메일 주소의 무단 수집을 거부합니다.',
    updatedAt: '2026-10-07',
  },
}

/** 사업자등록번호로 공정거래위원회 사업자정보 조회를 연다 */
export const BIZ_INFO_URL = `https://www.ftc.go.kr/bizCommPop.do?wrkr_no=${COMPANY.bizNumber.replace(/-/g, '')}`

export const PORTAL_LEGAL_LINKS: { type: LegalDocType; label: string; to: string; strong?: boolean }[] = [
  { type: 'service', label: '이용약관', to: '/terms/service' },
  { type: 'privacy', label: '개인정보처리방침', to: '/terms/privacy', strong: true },
  { type: 'privacy-consent', label: '개인정보 수집·이용 동의', to: '/terms/privacy-consent' },
  { type: 'refund', label: '환불 기준', to: '/terms/refund' },
  { type: 'email', label: '이메일 무단수집 거부', to: '/terms/email' },
]

export const TERMS_OF_SERVICE_SECTIONS = [
  {
    title: '제1조 (목적)',
    body: `본 약관은 ${COMPANY.name}(이하 "회사")가 운영하는 ${COMPANY.serviceName}(이하 "서비스")의 이용과 관련하여 회사와 회원 간의 권리·의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.`,
  },
  {
    title: '제2조 (정의)',
    body: `① "서비스"란 회사가 제공하는 장기요양기관 대상 임금·회계·고시 지원, 무료 상담 게시판, 회원 전용 기능 등 일체의 온라인 서비스를 말합니다.
② "회원"이란 본 약관에 동의하고 회사와 이용계약을 체결한 자를 말합니다.
③ "아이디(ID)"란 회원 식별과 서비스 이용을 위해 회원이 설정하고 회사가 승인한 문자·숫자 조합을 말합니다.
④ "기관 정보"란 회원이 소속된 장기요양기관의 명칭, 기관 기호 등 서비스 제공에 필요한 정보를 말합니다.`,
  },
  {
    title: '제3조 (약관의 효력 및 변경)',
    body: `① 본 약관은 서비스 화면에 게시하거나 기타 방법으로 회원에게 공지함으로써 효력이 발생합니다.
② 회사는 관련 법령을 위반하지 않는 범위에서 약관을 변경할 수 있으며, 변경 시 적용일자 및 변경 사유를 사전에 공지합니다.
③ 회원이 변경된 약관에 동의하지 않는 경우 서비스 이용을 중단하고 탈퇴할 수 있습니다.`,
  },
  {
    title: '제4조 (회원가입)',
    body: `① 회원가입은 이용자가 약관 및 개인정보 수집·이용에 동의하고 회사가 정한 절차에 따라 가입 신청을 하면, 회사가 이를 승낙함으로써 성립합니다.
② 회원은 가입 신청 시 진실한 정보를 제공해야 하며, 허위 정보 제공으로 발생한 불이익은 회원에게 있습니다.
③ 회사는 다음 각 호에 해당하는 경우 가입을 거부하거나 사후에 이용계약을 해지할 수 있습니다.
  1. 타인의 명의를 도용한 경우
  2. 허위 정보를 기재한 경우
  3. 만 14세 미만이 법정대리인 동의 없이 가입한 경우
  4. 기타 회사가 정한 이용신청 요건을 충족하지 못한 경우`,
  },
  {
    title: '제5조 (서비스의 제공)',
    body: `① 회사는 다음과 같은 서비스를 제공합니다.
  1. 장기요양기관 임금 구조 점검 및 진단 관련 정보 제공
  2. 회계·고시 관련 안내 및 지원 기능
  3. 무료 상담 게시판 및 상담 신청
  4. 기타 회사가 정하는 부가 서비스
② 서비스는 연중무휴 1일 24시간 제공함을 원칙으로 하나, 시스템 점검·장애 등 불가피한 사유가 있는 경우 일시 중단될 수 있습니다.
③ 회사는 서비스 개선을 위해 내용을 변경하거나 일부 기능을 추가·중단할 수 있습니다.`,
  },
  {
    title: '제6조 (회원의 의무)',
    body: `① 회원은 관계 법령, 본 약관, 서비스 이용안내 및 공지사항을 준수해야 합니다.
② 회원은 아이디·비밀번호를 안전하게 관리할 책임이 있으며, 제3자에게 양도·대여할 수 없습니다.
③ 회원은 서비스 이용 중 취득한 정보를 회사의 사전 동의 없이 영리 목적으로 이용하거나 제3자에게 제공할 수 없습니다.
④ 회원은 기관 정보, 임금대장 등 업로드 자료에 대한 적법한 이용 권한을 보유해야 합니다.`,
  },
  {
    title: '제7조 (회사의 의무)',
    body: `① 회사는 관련 법령과 본 약관이 정하는 바에 따라 지속적이고 안정적으로 서비스를 제공하기 위해 노력합니다.
② 회사는 회원의 개인정보를 보호하기 위해 개인정보처리방침을 수립·공개하고 이를 준수합니다.
③ 회사는 회원으로부터 제기된 의견이나 불만이 정당하다고 인정되는 경우 지체 없이 처리합니다.`,
  },
  {
    title: '제8조 (서비스 이용 제한)',
    body: `회사는 회원이 본 약관 또는 관계 법령을 위반하거나 서비스 운영을 방해한 경우 사전 통지 후 서비스 이용을 제한하거나 이용계약을 해지할 수 있습니다. 긴급한 경우 사후 통지할 수 있습니다.`,
  },
  {
    title: '제9조 (면책)',
    body: `① 회사는 천재지변, 불가항력, 회원의 귀책사유로 인한 서비스 장애에 대해 책임을 지지 않습니다.
② 회사가 제공하는 임금·회계·고시 관련 정보는 참고용이며, 최종 의사결정과 법적 책임은 회원에게 있습니다.
③ 회원이 게시판 등에 게시한 정보의 내용에 대한 책임은 회원에게 있습니다.`,
  },
  {
    title: '제10조 (분쟁 해결)',
    body: `① 회사와 회원 간 분쟁이 발생한 경우 상호 협의하여 해결합니다.
② 협의가 이루어지지 않을 경우 관련 법령 및 일반적인 상관례에 따릅니다.`,
  },
  {
    title: '부칙',
    body: `본 약관은 2026년 6월 22일부터 시행합니다.

문의: ${COMPANY.email}`,
  },
]

export const PRIVACY_POLICY_SECTIONS = [
  {
    title: '1. 개인정보의 처리 목적',
    body: `${COMPANY.name}는 다음 목적을 위해 개인정보를 처리합니다.
· 회원 가입 의사 확인, 회원제 서비스 제공, 본인 식별·인증
· 장기요양기관 맞춤 임금·회계·고시 지원 서비스 제공
· 무료 상담 신청 접수, 답변 및 고객 문의 대응
· 서비스 개선, 신규 서비스 안내(동의한 경우)
· 부정 이용 방지 및 서비스 안정성 확보`,
  },
  {
    title: '2. 처리하는 개인정보 항목',
    body: `회사는 서비스 제공을 위해 필요한 최소한의 개인정보만을 수집합니다. 상세 항목은 회원가입 시 개인정보 수집·이용 동의서 및 수집 항목 표를 참고해 주세요.`,
  },
  {
    title: '3. 개인정보의 보유 및 이용 기간',
    body: `회사는 개인정보 수집·이용 목적이 달성된 후에는 지체 없이 파기합니다. 다만 관계 법령에 따라 보존할 필요가 있는 경우 해당 기간 동안 보관합니다.
· 계약 또는 청약철회 등에 관한 기록: 5년
· 소비자 불만 또는 분쟁처리에 관한 기록: 3년
· 접속에 관한 기록: 3개월`,
  },
  {
    title: '4. 개인정보의 제3자 제공',
    body: `회사는 원칙적으로 회원의 개인정보를 제3자에게 제공하지 않습니다. 다만 법령에 근거하거나 수사기관의 적법한 절차에 따른 요청이 있는 경우 예외로 합니다.`,
  },
  {
    title: '5. 개인정보 처리의 위탁',
    body: `회사는 원활한 서비스 제공을 위해 다음과 같이 개인정보 처리 업무를 위탁할 수 있으며, 위탁 시 관련 법령에 따라 안전하게 관리·감독합니다.
· 이메일·SMS 발송: 인증 및 안내 메시지 전송
· 클라우드 인프라: 서비스 운영 및 데이터 보관`,
  },
  {
    title: '6. 정보주체의 권리',
    body: `회원은 언제든지 개인정보 열람, 정정, 삭제, 처리정지, 동의 철회를 요청할 수 있습니다. 요청은 ${COMPANY.email} 또는 상담 게시판으로 연락해 주시면 지체 없이 조치합니다.`,
  },
  {
    title: '7. 개인정보 보호책임자',
    body: `개인정보 보호책임자
· 상호: ${COMPANY.name}
· 서비스명: ${COMPANY.serviceName}
· 이메일: ${COMPANY.email}`,
  },
]

export const PRIVACY_CONSENT_SECTIONS = [
  {
    title: '개인정보 수집·이용 동의',
    body: `${COMPANY.name}(이하 "회사")는 ${COMPANY.serviceName} 회원가입 및 서비스 제공을 위해 아래와 같이 개인정보를 수집·이용합니다. 내용을 충분히 읽어보신 후 동의 여부를 결정해 주세요.`,
  },
  {
    title: '동의 거부 권리 및 불이익',
    body: `귀하는 개인정보 수집·이용에 대한 동의를 거부할 권리가 있습니다. 다만 필수 항목에 대한 동의를 거부할 경우 회원가입 및 서비스 이용이 제한될 수 있습니다.`,
  },
]

export const REFUND_POLICY_SECTIONS = [
  {
    title: '결제 전 취소',
    body: `토스페이먼츠 승인 전에는 구매자가 주문을 직접 취소할 수 있습니다. 이 단계에서는 돈이 빠져 나가지 않으며 환불도 없습니다.`,
  },
  {
    title: '결제 후 출고 전',
    body: `카드, 간편결제 또는 휴대폰결제로 결제한 주문은 출고 준비까지 상품별로 환불을 신청할 수 있습니다. 신청한 상품은 바로 취소됩니다.
환불은 토스페이먼츠 결제 취소로 처리합니다. 그 상품 금액이 결제했던 카드, 간편결제 또는 휴대폰결제로 돌아갑니다. 그 판매업체 상품이 이것까지면 그 업체 출고 배송비도 함께 취소됩니다. 다른 업체 상품이 남아 있으면 그 업체 배송비는 그대로입니다. 환불 계좌는 적지 않습니다.
결제 취소가 끝나면 카드사는 카드사 기준, 간편결제는 해당 결제사 기준, 휴대폰결제는 통신사 기준에 따라 영업일 기준 수일 안에 반영됩니다.`,
  },
  {
    title: '배송 중',
    body: `배송이 시작된 뒤에는 환불을 신청할 수 없습니다. 상품을 받은 뒤 반품으로 요청해 주세요.`,
  },
  {
    title: '배송 완료 후 반품',
    body: `카드, 간편결제 또는 휴대폰결제로 결제한 상품은 배송 완료 후 구매확정 전에 반품을 요청할 수 있습니다. 구매확정 뒤에는 단순 변심 반품을 요청할 수 없습니다. 상품 하자·오배송은 구매확정 뒤에도 요청할 수 있고, 승인되면 상품 금액 전액을 결제 취소합니다. 지급 전 정산 요청에 들어간 상품은 그 요청을 해제한 뒤에 요청합니다. 이미 지급된 상품의 하자·오배송 환불은 이후 정산에서 차감합니다.
반품이 승인되면 그 상품 금액을 토스페이먼츠 결제 취소로 돌려줍니다. 주문 안 일부 상품만 반품하면 그 금액만 부분 취소합니다. 돈은 결제했던 카드, 간편결제 또는 휴대폰결제로 돌아갑니다.
단순 변심 반품 배송비는 상품마다 다릅니다. 출고 배송비는 판매가격에 포함되어 있고, 환불할 때는 그 상품에 적힌 수량마다 배송비를 받습니다. 수량이 그 기준 이하면 한 번만 받고, 넘치면 올림합니다. 예를 들어 10개당 5,000원이면 10개 이하는 5,000원이고, 236개는 24 × 5,000원입니다. 그 금액을 뺀 나머지가 결제 취소됩니다. 받은 반품 배송비는 판매업체 정산에 포함됩니다. 상품 하자·오배송은 상품 금액 전액이 결제 취소되고, 상품 대금은 판매업체 정산에서 빠집니다. 처음 판매가격에 포함했던 출고 배송비는 반품 환불에서 따로 빼지 않습니다. 반품 배송비가 상품 금액 이상이면 결제 취소 금액은 0원입니다.
반영 시점은 카드사, 간편결제사 또는 통신사 기준에 따릅니다.`,
  },
  {
    title: '이전 계좌이체 주문',
    body: `계좌이체로 이미 결제한 주문은 그 판매기록 그대로 둡니다. 새 주문은 계좌이체를 받지 않습니다.
그 주문의 출고 전 환불과 배송 완료 후 반품은 운영팀이 구매자가 적은 계좌로 송금합니다. 금액 계산은 당시와 같습니다. 단순 변심은 반품 배송비를 뺀 나머지, 상품 하자·오배송은 상품 금액 전액입니다. 구매확정 뒤에도 하자·오배송은 요청할 수 있습니다.`,
  },
  {
    title: '문의',
    body: `환불·반품 문의는 ${COMPANY.email} 로 보내 주세요. ${COMPANY.hours}`,
  },
]

export const EMAIL_REJECTION_SECTIONS = [
  {
    title: '무단 수집 거부',
    body: `${COMPANY.serviceName}와 ${COMPANY.mallName}에 게시된 이메일 주소가 전자우편 수집 프로그램이나 그 밖의 기술적 장치를 이용하여 무단으로 수집되는 것을 거부합니다.`,
  },
  {
    title: '위반 시',
    body: `이를 위반하여 이메일 주소를 무단으로 수집·판매·유통하는 경우 정보통신망 이용촉진 및 정보보호 등에 관한 법률에 따라 처벌될 수 있습니다.`,
  },
  {
    title: '문의',
    body: `${COMPANY.legalName}
대표 ${COMPANY.owner}
이메일 ${COMPANY.email}`,
  },
]

export const MARKETING_CONSENT_SECTIONS = [
  {
    title: '수신 항목 및 방법',
    body: `회사는 장기요양기관 운영에 도움이 되는 신규 서비스, 고시·임금 관련 안내, 이벤트 및 프로모션 정보를 아래 방법으로 발송할 수 있습니다.
· 수신 채널: 이메일, SMS·알림톡, 서비스 내 공지
· 발송 내용: 서비스 업데이트, 무료 진단·상담 안내, 교육·세미나 초대 등`,
  },
  {
    title: '보유 및 이용 기간',
    body: `동의일로부터 회원 탈퇴 또는 수신 동의 철회 시까지 보유·이용합니다.`,
  },
  {
    title: '동의 철회',
    body: `마케팅 수신 동의는 선택 사항이며, 언제든지 ${COMPANY.email} 또는 서비스 내 설정을 통해 철회할 수 있습니다.`,
  },
]
